import * as THREE from 'three';
import type { ExperienceCue, ThoughtIdentityConfig } from '../content/types';
import { AssessmentPlaceholder } from '../graphics/AssessmentPlaceholder';
import { ThoughtField } from '../graphics/ThoughtField';

export type WorldMode = 'vestibule' | 'entering' | 'immersive' | 'future-preview';

export class ClinicalWorld {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene = new THREE.Scene();
  readonly camera = new THREE.PerspectiveCamera(68, 16 / 9, 0.05, 60);
  mode: WorldMode = 'vestibule';
  frame = 0;
  private readonly vestibule = new THREE.Group();
  private readonly immersive = new THREE.Group();
  private readonly assessment = new AssessmentPlaceholder();
  private readonly thoughtField = new ThoughtField();
  private thoughtTime = 0;
  private readonly resizeObserver: ResizeObserver;
  private readonly startPosition = new THREE.Vector3(0, 1.64, 5.4);
  private yaw = 0;
  private pitch = 0;
  private pointerX = 0;
  private pointerY = 0;
  private dragging = false;
  private transitionStarted = 0;
  private transitionSwitched = false;
  private transitionTarget = new THREE.Vector3();
  private transitionLook = new THREE.Vector3();
  private onModeChange?: (mode: WorldMode) => void;
  private reducedMotion = false;

  constructor(private readonly canvas: HTMLCanvasElement, panoramaTexture: THREE.Texture) {
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, powerPreference: 'high-performance' });
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 0.98;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFShadowMap;
    this.scene.background = new THREE.Color('#152456');
    this.scene.fog = new THREE.Fog('#152456', 9, 24);
    this.camera.position.copy(this.startPosition);
    this.camera.lookAt(0, 1.55, -1.6);
    this.scene.add(this.vestibule, this.immersive);
    this.buildVestibule(panoramaTexture);
    this.buildImmersiveRoom(panoramaTexture);
    this.immersive.visible = false;

    this.canvas.addEventListener('pointerdown', this.onPointerDown);
    this.canvas.addEventListener('pointermove', this.onPointerMove);
    this.canvas.addEventListener('pointerup', this.onPointerUp);
    this.canvas.addEventListener('pointercancel', this.onPointerUp);
    this.canvas.addEventListener('wheel', this.onWheel, { passive: false });
    window.addEventListener('keydown', this.onKeyDown);
    this.resizeObserver = new ResizeObserver(this.resize);
    this.resizeObserver.observe(canvas.parentElement ?? canvas);
    this.resize();
  }

  setModeListener(listener: (mode: WorldMode) => void): void {
    this.onModeChange = listener;
  }

  setReducedMotion(reduced: boolean): void {
    this.reducedMotion = reduced;
    this.assessment.setReducedMotion(reduced);
    this.thoughtField.setReducedMotion(reduced);
  }

  configureThoughtIdentity(config: ThoughtIdentityConfig): void { this.thoughtField.configure(config); }

  enterMorning(): void {
    if (this.mode !== 'vestibule' && this.mode !== 'future-preview') return;
    this.mode = 'entering';
    this.transitionStarted = performance.now() / 1000;
    this.transitionSwitched = false;
    this.transitionTarget.set(-1.88, 1.62, 0.1);
    this.transitionLook.set(-1.88, 1.55, -2.5);
    this.onModeChange?.(this.mode);
    if (this.reducedMotion) this.switchToImmersive();
  }

  previewFuture(): void {
    if (this.mode !== 'vestibule') return;
    this.mode = 'future-preview';
    this.transitionStarted = performance.now() / 1000;
    this.transitionTarget.set(1.9, 1.62, 1.8);
    this.transitionLook.set(1.9, 1.55, -2.3);
    this.onModeChange?.(this.mode);
    if (this.reducedMotion) {
      this.camera.position.copy(this.transitionTarget);
      this.camera.lookAt(this.transitionLook);
    }
  }

  returnToVestibule(): void {
    this.mode = 'vestibule';
    this.vestibule.visible = true;
    this.immersive.visible = false;
    this.camera.position.copy(this.startPosition);
    this.camera.fov = 68;
    this.camera.updateProjectionMatrix();
    this.camera.lookAt(0, 1.55, -1.6);
    this.setCue(null);
    this.onModeChange?.(this.mode);
  }

  recenter(): void {
    this.yaw = 0;
    this.pitch = 0;
    if (this.mode === 'immersive') this.applyImmersiveRotation();
  }

  setCue(cue: ExperienceCue | null): void {
    this.assessment.setVisible(cue?.kind === 'assessment');
    this.thoughtField.setCue(cue);
  }

  setAssessmentMedia(video: HTMLVideoElement | null): void {
    this.assessment.setMedia(video);
  }

  update(delta: number, elapsed: number, audioEnergy: number, playing = true): void {
    this.frame += 1;
    if (this.mode === 'entering' && !this.reducedMotion) {
      const progress = Math.min(1, (elapsed - this.transitionStarted) / 2.35);
      if (!this.transitionSwitched) {
        const eased = easeInOutCubic(Math.min(1, progress / 0.58));
        this.camera.position.lerpVectors(this.startPosition, this.transitionTarget, eased);
        this.camera.lookAt(this.transitionLook);
        if (progress >= 0.58) this.switchToImmersive();
      }
    } else if (this.mode === 'future-preview' && !this.reducedMotion) {
      const progress = Math.min(1, (elapsed - this.transitionStarted) / 1.6);
      this.camera.position.lerpVectors(this.startPosition, this.transitionTarget, easeInOutCubic(progress));
      this.camera.lookAt(this.transitionLook);
    }

    if (this.mode === 'immersive') {
      this.assessment.update(elapsed, audioEnergy);
      if (playing) this.thoughtTime += delta;
      this.thoughtField.update(this.thoughtTime, audioEnergy, playing);
    } else if (!this.reducedMotion) {
      const float = Math.sin(elapsed * 0.45) * 0.018;
      this.camera.position.y = (this.mode === 'vestibule' ? this.startPosition.y : this.camera.position.y) + float * delta * 12;
    }
    this.renderer.render(this.scene, this.camera);
  }

  diagnostics(): Record<string, unknown> {
    const info = this.renderer.info;
    return {
      mode: this.mode,
      frame: this.frame,
      renderer: {
        calls: info.render.calls,
        triangles: info.render.triangles,
        lines: info.render.lines,
        geometries: info.memory.geometries,
        textures: info.memory.textures,
        dpr: this.renderer.getPixelRatio(),
      },
      camera: { yaw: this.yaw, pitch: this.pitch, fov: this.camera.fov },
    };
  }

  dispose(): void {
    this.resizeObserver.disconnect();
    this.canvas.removeEventListener('pointerdown', this.onPointerDown);
    this.canvas.removeEventListener('pointermove', this.onPointerMove);
    this.canvas.removeEventListener('pointerup', this.onPointerUp);
    this.canvas.removeEventListener('pointercancel', this.onPointerUp);
    this.canvas.removeEventListener('wheel', this.onWheel);
    window.removeEventListener('keydown', this.onKeyDown);
    this.assessment.dispose();
    this.scene.traverse((object) => {
      if (!(object instanceof THREE.Mesh || object instanceof THREE.Line)) return;
      object.geometry.dispose();
      const materials = Array.isArray(object.material) ? object.material : [object.material];
      for (const material of materials) {
        if ('map' in material) (material.map as THREE.Texture | null)?.dispose();
        material.dispose();
      }
    });
    this.renderer.dispose();
  }

  private switchToImmersive(): void {
    this.transitionSwitched = true;
    this.vestibule.visible = false;
    this.immersive.visible = true;
    this.camera.position.set(0, 0.08, 0);
    this.camera.fov = this.fittedFieldOfView();
    this.camera.updateProjectionMatrix();
    this.mode = 'immersive';
    this.applyImmersiveRotation();
    this.onModeChange?.(this.mode);
  }

  private buildVestibule(panoramaTexture: THREE.Texture): void {
    const material = {
      floor: new THREE.MeshStandardMaterial({ color: '#8e9089', roughness: 0.9, metalness: 0.01 }),
      wall: new THREE.MeshStandardMaterial({ color: '#dfe1df', roughness: 0.98 }),
      trim: new THREE.MeshStandardMaterial({ color: '#152456', roughness: 0.7, metalness: 0.04 }),
      dark: new THREE.MeshStandardMaterial({ color: '#152456', roughness: 0.82, metalness: 0.04 }),
      amber: new THREE.MeshStandardMaterial({ color: '#4e5259', roughness: 0.62, emissive: '#152456', emissiveIntensity: 0.09 }),
      steel: new THREE.MeshStandardMaterial({ color: '#8e9089', roughness: 0.46, metalness: 0.38 }),
    };

    const floor = new THREE.Mesh(new THREE.PlaneGeometry(10, 11), material.floor);
    floor.rotation.x = -Math.PI / 2;
    floor.position.set(0, 0, 0.8);
    floor.receiveShadow = true;
    this.vestibule.add(floor);
    const ceiling = new THREE.Mesh(new THREE.PlaneGeometry(10, 11), material.wall);
    ceiling.rotation.x = Math.PI / 2;
    ceiling.position.set(0, 3.45, 0.8);
    this.vestibule.add(ceiling);

    const sideGeometry = new THREE.BoxGeometry(0.28, 3.45, 11);
    for (const x of [-4.86, 4.86]) {
      const wall = new THREE.Mesh(sideGeometry, material.wall);
      wall.position.set(x, 1.72, 0.8);
      wall.receiveShadow = true;
      this.vestibule.add(wall);
      const rail = new THREE.Mesh(new THREE.BoxGeometry(0.07, 0.2, 10.7), material.trim);
      rail.position.set(x + (x < 0 ? 0.18 : -0.18), 1.02, 0.8);
      this.vestibule.add(rail);
    }

    const backSegments = [
      { x: -4.05, w: 1.3 }, { x: 0, w: 1.45 }, { x: 4.05, w: 1.3 },
    ];
    for (const segment of backSegments) {
      const wall = new THREE.Mesh(new THREE.BoxGeometry(segment.w, 3.45, 0.26), material.wall);
      wall.position.set(segment.x, 1.72, -1.0);
      wall.castShadow = true;
      wall.receiveShadow = true;
      this.vestibule.add(wall);
    }
    const header = new THREE.Mesh(new THREE.BoxGeometry(6.9, 0.55, 0.3), material.wall);
    header.position.set(0, 3.17, -1);
    this.vestibule.add(header);

    this.vestibule.add(this.createPortal(-1.88, 'ROOM 01', 'MORNING ENCOUNTER', panoramaTexture, false, material));
    this.vestibule.add(this.createPortal(1.88, 'ROOM 02', 'IN PRODUCTION', panoramaTexture, true, material));
    this.vestibule.add(this.createBench(-4.1, 0.48, 2.65, material));

    const pathCurve = new THREE.CatmullRomCurve3([
      new THREE.Vector3(0, 0.012, 5.7),
      new THREE.Vector3(-0.25, 0.012, 3.1),
      new THREE.Vector3(-1.35, 0.012, 0.4),
      new THREE.Vector3(-1.88, 0.012, -0.72),
    ]);
    const path = new THREE.Mesh(new THREE.TubeGeometry(pathCurve, 48, 0.025, 8, false), material.amber);
    this.vestibule.add(path);

    const key = new THREE.DirectionalLight('#ffffff', 1.02);
    key.position.set(-3, 6, 4);
    key.castShadow = true;
    key.shadow.mapSize.set(1024, 1024);
    key.shadow.camera.left = -7; key.shadow.camera.right = 7; key.shadow.camera.top = 7; key.shadow.camera.bottom = -7;
    this.vestibule.add(key, new THREE.HemisphereLight('#dfe1df', '#152456', 0.58));
    for (let index = 0; index < 5; index += 1) {
      const fixture = new THREE.Mesh(new THREE.BoxGeometry(1.45, 0.04, 0.55), new THREE.MeshBasicMaterial({ color: '#ffffff' }));
      fixture.position.set(index % 2 ? 2.1 : -2.1, 3.39, 4.4 - index * 1.25);
      this.vestibule.add(fixture);
    }
  }

  private createPortal(
    x: number,
    room: string,
    label: string,
    panoramaTexture: THREE.Texture,
    future: boolean,
    materials: Record<string, THREE.MeshStandardMaterial>,
  ): THREE.Group {
    const portal = new THREE.Group();
    portal.position.set(x, 0, -1.03);
    const vertical = new THREE.BoxGeometry(0.16, 3.08, 0.24);
    const horizontal = new THREE.BoxGeometry(2.5, 0.16, 0.24);
    for (const px of [-1.17, 1.17]) {
      const jamb = new THREE.Mesh(vertical, materials.steel);
      jamb.position.set(px, 1.54, 0);
      jamb.castShadow = true;
      portal.add(jamb);
    }
    const lintel = new THREE.Mesh(horizontal, materials.steel);
    lintel.position.set(0, 3.03, 0);
    portal.add(lintel);

    const previewMaterial = future
      ? new THREE.MeshPhysicalMaterial({ color: '#dfe1df', transmission: 0.08, transparent: true, opacity: 0.76, roughness: 0.52 })
      : new THREE.MeshBasicMaterial({ map: panoramaTexture, toneMapped: false });
    const preview = new THREE.Mesh(new THREE.PlaneGeometry(2.18, future ? 2.82 : 1.226), previewMaterial);
    preview.position.set(0, future ? 1.54 : 1.66, -0.05);
    preview.name = future ? 'future-room-frosted-glass' : 'morning-room-video-portal';
    portal.add(preview);

    const roomShell = new THREE.Mesh(new THREE.BoxGeometry(2.72, 3.2, 3.5), new THREE.MeshStandardMaterial({ color: future ? '#4e5259' : '#dfe1df', side: THREE.BackSide, roughness: 0.94 }));
    roomShell.position.set(0, 1.55, -1.75);
    portal.add(roomShell);
    portal.add(this.createExamChair(future ? 0.2 : -0.32, future ? materials.dark : materials.trim));

    const plaqueTexture = new THREE.CanvasTexture(drawPlaque(room, label, future));
    plaqueTexture.colorSpace = THREE.SRGBColorSpace;
    const plaque = new THREE.Mesh(new THREE.PlaneGeometry(1.55, 0.52), new THREE.MeshBasicMaterial({ map: plaqueTexture, transparent: true }));
    plaque.position.set(0, 3.48, 0.05);
    portal.add(plaque);
    return portal;
  }

  private createExamChair(x: number, upholstery: THREE.Material): THREE.Group {
    const chair = new THREE.Group();
    chair.position.set(x, 0.1, -2.15);
    const seat = new THREE.Mesh(new THREE.CapsuleGeometry(0.42, 1.0, 4, 12), upholstery);
    seat.rotation.z = Math.PI / 2;
    seat.rotation.x = -0.18;
    seat.position.set(0, 0.92, 0);
    chair.add(seat);
    const back = new THREE.Mesh(new THREE.CapsuleGeometry(0.38, 0.86, 4, 12), upholstery);
    back.rotation.z = Math.PI / 2;
    back.rotation.y = -0.78;
    back.position.set(0, 1.45, -0.42);
    chair.add(back);
    for (const lx of [-0.36, 0.36]) {
      const leg = new THREE.Mesh(new THREE.CylinderGeometry(0.035, 0.045, 0.82, 10), new THREE.MeshStandardMaterial({ color: '#8e9089', metalness: 0.48, roughness: 0.38 }));
      leg.position.set(lx, 0.44, 0);
      leg.rotation.z = lx * 0.2;
      chair.add(leg);
    }
    return chair;
  }

  private createBench(x: number, y: number, z: number, materials: Record<string, THREE.MeshStandardMaterial>): THREE.Group {
    const bench = new THREE.Group();
    bench.position.set(x, y, z);
    const cushion = new THREE.Mesh(new THREE.CapsuleGeometry(0.28, 1.1, 4, 12), materials.trim);
    cushion.rotation.z = Math.PI / 2;
    cushion.castShadow = true;
    bench.add(cushion);
    const support = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 0.72, 10), materials.steel);
    support.position.y = -0.35;
    bench.add(support);
    return bench;
  }

  private buildImmersiveRoom(panoramaTexture: THREE.Texture): void {
    // The web experience uses a focused 100-degree extraction instead of
    // asking every browser to decode and navigate an 8K panorama. A shallow
    // curved screen preserves a sense of presence without distorting faces.
    const screenGeometry = curveFocusedScreen(new THREE.PlaneGeometry(9.2, 5.175, 48, 2), 0.42);
    const screen = new THREE.Mesh(
      screenGeometry,
      new THREE.MeshBasicMaterial({ map: panoramaTexture, toneMapped: false }),
    );
    screen.position.set(0, 0.08, -5.65);
    screen.name = 'focused-conversation-screen';
    this.immersive.add(screen, this.assessment.group, this.thoughtField.group);

    const surround = new THREE.Mesh(
      new THREE.PlaneGeometry(13.5, 8),
      new THREE.MeshBasicMaterial({ color: '#101b41' }),
    );
    surround.position.set(0, 0.1, -6.12);
    this.immersive.add(surround);

    const frameMaterial = new THREE.MeshBasicMaterial({ color: '#4e5259', transparent: true, opacity: 0.12 });
    for (const y of [-2.56, 2.72]) {
      const edge = new THREE.Mesh(new THREE.BoxGeometry(9.3, 0.018, 0.025), frameMaterial);
      edge.position.set(0, y, -5.15);
      this.immersive.add(edge);
    }
    const floor = new THREE.Mesh(
      new THREE.PlaneGeometry(16, 12),
      new THREE.MeshBasicMaterial({ color: '#152456', transparent: true, opacity: 0.94 }),
    );
    floor.rotation.x = -Math.PI / 2;
    floor.position.set(0, -2.65, -1.2);
    this.immersive.add(floor);
  }

  private applyImmersiveRotation(): void {
    this.camera.rotation.order = 'YXZ';
    this.camera.rotation.y = this.yaw;
    this.camera.rotation.x = this.pitch;
    this.camera.rotation.z = 0;
  }

  private readonly resize = (): void => {
    const width = Math.max(1, this.canvas.clientWidth);
    const height = Math.max(1, this.canvas.clientHeight);
    const mobile = width < 720;
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, mobile ? 1.5 : 2));
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.assessment.group.scale.setScalar(mobile ? 0.7 : 1);
    if (this.mode === 'immersive') this.camera.fov = this.fittedFieldOfView();
    this.camera.updateProjectionMatrix();
  };

  private fittedFieldOfView(): number {
    // Preserve both people and their overhead graphics when the viewport narrows.
    const halfWidth = 4.95;
    return Math.max(64, THREE.MathUtils.radToDeg(2 * Math.atan(halfWidth / (5.23 * this.camera.aspect))));
  }

  private readonly onPointerDown = (event: PointerEvent): void => {
    if (this.mode !== 'immersive') return;
    this.dragging = true;
    this.pointerX = event.clientX;
    this.pointerY = event.clientY;
    this.canvas.setPointerCapture(event.pointerId);
    this.canvas.classList.add('is-dragging');
  };

  private readonly onPointerMove = (event: PointerEvent): void => {
    if (!this.dragging || this.mode !== 'immersive') return;
    this.yaw = THREE.MathUtils.clamp(this.yaw - (event.clientX - this.pointerX) * 0.00125, -0.14, 0.14);
    this.pitch = THREE.MathUtils.clamp(this.pitch - (event.clientY - this.pointerY) * 0.001, -0.08, 0.08);
    this.pointerX = event.clientX;
    this.pointerY = event.clientY;
    this.applyImmersiveRotation();
  };

  private readonly onPointerUp = (event: PointerEvent): void => {
    this.dragging = false;
    this.canvas.classList.remove('is-dragging');
    if (this.canvas.hasPointerCapture(event.pointerId)) this.canvas.releasePointerCapture(event.pointerId);
  };

  private readonly onWheel = (event: WheelEvent): void => {
    if (this.mode !== 'immersive') return;
    event.preventDefault();
    const fitted = this.fittedFieldOfView();
    this.camera.fov = THREE.MathUtils.clamp(this.camera.fov + Math.sign(event.deltaY) * 2, Math.max(54, fitted - 10), Math.max(82, fitted));
    this.camera.updateProjectionMatrix();
  };

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (this.mode !== 'immersive' || event.target instanceof HTMLInputElement || event.target instanceof HTMLButtonElement) return;
    if (event.shiftKey) return;
    const amount = 0.035;
    if (event.code === 'ArrowLeft') this.yaw = THREE.MathUtils.clamp(this.yaw + amount, -0.14, 0.14);
    else if (event.code === 'ArrowRight') this.yaw = THREE.MathUtils.clamp(this.yaw - amount, -0.14, 0.14);
    else if (event.code === 'ArrowUp') this.pitch = THREE.MathUtils.clamp(this.pitch + amount, -0.08, 0.08);
    else if (event.code === 'ArrowDown') this.pitch = THREE.MathUtils.clamp(this.pitch - amount, -0.08, 0.08);
    else return;
    event.preventDefault();
    this.applyImmersiveRotation();
  };
}

function curveFocusedScreen(geometry: THREE.PlaneGeometry, amount: number): THREE.PlaneGeometry {
  const positions = geometry.attributes.position;
  for (let index = 0; index < positions.count; index += 1) {
    const x = positions.getX(index);
    positions.setZ(index, amount * Math.pow(x / 4.6, 2));
  }
  positions.needsUpdate = true;
  geometry.computeVertexNormals();
  return geometry;
}

function drawPlaque(room: string, label: string, future: boolean): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = 900;
  canvas.height = 300;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = future ? 'rgba(21, 36, 86, .94)' : 'rgba(255, 255, 255, .97)';
  ctx.roundRect(8, 8, 884, 284, 28);
  ctx.fill();
  ctx.strokeStyle = future ? '#59b7df' : '#4e5259';
  ctx.lineWidth = 8;
  ctx.stroke();
  ctx.fillStyle = future ? '#eaf5fb' : '#152456';
  ctx.font = '700 42px Arial';
  ctx.fillText(room, 54, 91);
  ctx.fillStyle = future ? '#dfe1df' : '#152456';
  ctx.font = '700 58px Arial';
  ctx.fillText(label, 54, 172);
  ctx.fillStyle = future ? '#dfe1df' : '#4e5259';
  ctx.font = '28px Arial';
  ctx.fillText(future ? 'CONTENT NOT YET FILMED' : 'ENTER TO OBSERVE', 54, 234);
  return canvas;
}

function easeInOutCubic(value: number): number {
  return value < 0.5 ? 4 * value * value * value : 1 - Math.pow(-2 * value + 2, 3) / 2;
}
